<template>
    <v-tabs v-model="tabActive" background-color="blue-grey lighten-4" slider-color="white">
        <v-tab href="#info" ripple>{{ $t("tab.info") }}</v-tab>
        <v-tab href="#status" ripple>{{ $t("tab.status_history") }}</v-tab>
        <v-tab
            href="#location"
            v-if="
                (trap.geoLocation && trap.geoLocation.lat) ||
                (trap.planLocation && trap.planLocation.lat)
            "
            ripple
            >{{ $t("tab.location") }}</v-tab
        >
        <v-tab href="#statistics" ripple>{{ $t("tab.statistics") }}</v-tab>
        <v-tabs-items v-model="tabActive" touchless>
            <v-tab-item value="info">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <strong>
                            <v-icon left small>arrow_forward_ios</v-icon>
                            {{ $t("label.serial_number") }}: {{ trap.serialNumber }}
                        </strong>
                        <br />
                        <br />
                        <v-btn
                            block
                            color="primary"
                            :to="{ name: 'trap-edit', params: { id: trap._id } }"
                            rounded
                            outlined
                        >
                            {{ $t("button.edit") }}
                            <v-icon right>edit</v-icon>
                        </v-btn>
                        <v-layout>
                            <v-menu
                                offset-y
                                v-for="category in trap.categories"
                                :key="category._id"
                            >
                                <template v-slot:activator="{ on }">
                                    <v-chip outlined color="black" label v-on="on">{{
                                        category.name
                                    }}</v-chip>
                                </template>
                                <v-card style="max-width: 600px;">
                                    <v-card-text
                                        class="text--primary"
                                        v-html="$nl2br(category.description)"
                                    ></v-card-text>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn
                                            color="primary"
                                            rounded
                                            text
                                            :to="{
                                                name: 'categories-id',
                                                params: { id: category._id },
                                            }"
                                        >
                                            {{ $t("button.edit") }}
                                            <v-icon small right>edit</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-menu>
                        </v-layout>
                        <template v-if="trap.status">
                            <v-chip
                                :text-color="getBatteryColor(trap.status.battery)"
                                class="chip-battery hidden-md-and-up"
                                outlined
                            >
                                <v-avatar>
                                    <v-icon>battery_full</v-icon>
                                </v-avatar>
                                {{ trap.status.battery }}%
                            </v-chip>
                            <v-chip
                                :text-color="getBatteryColor(trap.status.signal)"
                                class="chip-battery hidden-md-and-up"
                                outlined
                            >
                                <v-avatar>
                                    <v-icon>signal_cellular_alt</v-icon>
                                </v-avatar>
                                {{ trap.status.signal }}%
                            </v-chip>
                        </template>
                    </v-card-text>
                </v-card>
            </v-tab-item>

            <v-tab-item value="status">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <TrapStatusList
                            v-if="tabActive == 'status'"
                            :trap="trap._id"
                            :key="trap._id + trap.updatedAt"
                        />
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item
                value="location"
                v-if="
                    (trap.geoLocation && trap.geoLocation.lat) ||
                    (trap.planLocation && trap.planLocation.lat)
                "
            >
                <v-card flat v-if="tabActive == 'location'">
                    <v-card-text
                        class="text--primary"
                        v-if="trap.geoLocation && trap.geoLocation.lat"
                    >
                        <GeoLocatedMap
                            readonly
                            class="mb-3"
                            :location="trap.geoLocation"
                            :locked-position="true"
                        />
                        <v-btn
                            outlined
                            rounded
                            v-t="'button.see_global_map'"
                            :to="{ name: 'map' }"
                        ></v-btn>
                    </v-card-text>
                    <v-card-text
                        class="text--primary"
                        v-if="trapDetail.plan && trap.planLocation && trap.planLocation.lat"
                    >
                        <h2>{{ trapDetail.plan.name }}</h2>
                        <div style="height: 400px; width: 100%;">
                            <Leaflet
                                :zoom="4"
                                v-if="trapDetail"
                                :plan="trapDetail.plan"
                                :value="trap.planLocation"
                            />
                        </div>

                        <v-btn
                            outlined
                            rounded
                            v-t="'button.see_all_trap_on_plan'"
                            :to="{ name: 'plan-view', params: { id: trapDetail.plan._id } }"
                        ></v-btn>
                    </v-card-text>
                </v-card>
            </v-tab-item>
            <v-tab-item value="statistics">
                <v-card flat>
                    <v-card-text class="text--primary">
                        <SingleTrapStatistics
                            :trap-id="trap._id"
                            :trapName="trap.name"
                            v-if="tabActive == 'statistics'"
                        />
                    </v-card-text>
                </v-card>
            </v-tab-item>
        </v-tabs-items>
    </v-tabs>
</template>

<script>
import GeoLocatedMap from "./GeoLocatedMap"
import SingleTrapStatistics from "./statistics/SingleTrap"
import Leaflet from "./Leaflet"
import TrapStatusList from "./TrapStatusList"
import { mapGetters } from "vuex"
export default {
    components: { GeoLocatedMap, TrapStatusList, Leaflet, SingleTrapStatistics },
    props: {
        trap: { type: Object, required: true },
    },
    apollo: {
        trapDetail: {
            query: require("@/graphql/trap/trap.gql"),
            fetchPolicy: "network-only",
            variables() {
                return {
                    _id: this.trap._id,
                }
            },
            update({ trap }) {
                return trap
            },
            skip() {
                return !this.trap.planLocation || !this.trap.planLocation.lat
            },
        },
    },
    computed: {
        ...mapGetters({ darkMode: "user/darkMode" }),
    },
    methods: {
        getBatteryColor(percent) {
            if (!percent) {
                return "red"
            }

            if (percent > 60) {
                return "green"
            }
            if (percent > 40) {
                return "amber darken-1"
            }
            if (percent > 20) {
                return "orange"
            }
            return "red"
        },
    },
    data() {
        return {
            tabActive: "info",
            trapDetail: {},
        }
    },
}
</script>
